import React, { Children, useCallback, useMemo, useState } from "react";
import Table from "react-bootstrap/Table";
import styled, { withTheme, css } from "styled-components";
import { debounce } from "../../../modules/utils";
import "./DefaultTable.css";
import { tablePageSizes, orderType } from "./constants";
import TablePagination from "./TablePagination";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import LoadingSpinner from "../../ui-library/LoadingSpinner/LoadingSpinner";
import { Row, Col } from "react-bootstrap";

export const ExpandableLine = (props) => {
  return props.children;
};

export const Line = (props) => {
  return props.children;
};

export const Filter = (props) => {
  return props.children;
};

export const ActionButtons = (props) => {
  return props.children;
};

export const FormInline = styled.form`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: ${(props) => props.gap};
`;

export const FilterCol = styled(Col)`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
`;

const StyledCol = styled(Col)`
  &:first-child {
    white-space: nowrap;
    margin: auto;
  }
`;

const StyledInfo = styled.div`
  font-size: 14px;
  text-align: center;
  width: 100%;
`;

export const ExpandableLines = (props) => {
  const { children, headers, lineIndex } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  const drawChild = (child, i) => (
    <tr key={`c-${i}`}>
      {child && <TableLine />}
      {child?.props?.children?.map((child, index) => (
        <TableLine key={index} responsivetable={1} isChild isExpandable>
          <TableLineHeader>{headers[index].label}</TableLineHeader>
          <TableLineChild>{child}</TableLineChild>
        </TableLine>
      ))}
    </tr>
  );

  const renderChildren = (c) => {
    if (Array.isArray(c)) {
      return c.map((elem, i) => drawChild(elem, i));
    }
  };

  let restOfChildren = [...children];
  restOfChildren.splice(0, 1);

  const showArrow = (children) => {
    return children && children.length > 0 && Array.isArray(children[1]);
  };

  return (
    <ExpandableLine>
      <StyledParentRow
        {...children[0]?.props}
        data-testid={`expandable-line-${lineIndex}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <TableLineArrow>
          {showArrow(children) && (
            <i className={`fas fa-angle-${isExpanded ? "up" : "down"}`}></i>
          )}
        </TableLineArrow>
        {children[0]?.props?.children?.map((child, index) => (
          <TableLine key={index} responsivetable={1} isExpandable>
            <TableLineHeader>{headers[index].label}</TableLineHeader>
            <TableLineChild>{child}</TableLineChild>
          </TableLine>
        ))}
      </StyledParentRow>

      {isExpanded && restOfChildren.map((c, index) => renderChildren(c))}
    </ExpandableLine>
  );
};

const TableActions = styled(Row)`
  gap: 20px;
  align-items: center;
`;

const StyledTable = styled(Table)`
  ${(props) =>
    props.$noLineBorder &&
    css`
      & > :not(:first-child) {
        border-top: 0;
      }
    `}

  ${(props) =>
    props.$fixedLayout &&
    css`
      table-layout: fixed;
    `}

  .cursor-pointer {
    cursor: pointer;
  }

  span {
    display: block;
  }

  th i.silver {
    color: ${(props) => props.theme?.silver};
  }

  td {
    font-size: 0.75rem;
    vertical-align: middle;
  }

  thead th,
  thead td {
    border-top: 1px transparent;
    font-weight: ${(props) => (props.$smallTable ? "600" : "bold")};
    font-size: ${(props) => (props.$smallTable ? "0.75rem" : "")};
  }

  th,
  td {
    ${(props) =>
      props.$noLineBorder
        ? "border-bottom: none;"
        : "border-bottom: 1px solid #dee2e6;"};
    padding: ${(props) => (props.$smallTable ? "0.20rem" : "")};
  }

  thead th {
    ${(props) =>
      props.$noLineBorder
        ? "border-bottom: none;"
        : "border-bottom: 2px solid #dee2e6;"}
  }

  ${(props) =>
    props?.responsivetable === 1 &&
    css`
      @media only screen and (max-width: 1023px) {
        /* Force table to not be like tables anymore */
        table,
        thead,
        tbody,
        th,
        td,
        tr {
          display: block;
        }

        /* Hide table headers (but not display: none;, for accessibility) */
        thead tr {
          position: absolute;
          top: -9999px;
          left: -9999px;
        }

        tr {
          margin: 0 0 1rem 0;
        }

        tr:nth-child(odd) {
          background: ${(props) =>
            props.isExpandable ? props.theme?.whiteSmoke : ""};
        }
      }
    `}
`;

const StyledParentRow = styled.tr`
  background-color: ${(props) => props.theme?.whiteSmoke};
  cursor: pointer;
`;

const TableLineArrow = styled.td`
  text-align: center;
`;

const TableLine = styled.td`
  ${(props) =>
    props.responsivetable === 1 &&
    css`
      @media only screen and (max-width: 1023px) {
        display: flex !important;
        flex-wrap: nowrap;
        align-items: center;
        gap: 8px;
      }
    `}

  ${(props) =>
    props.isChild &&
    css`
      background-color: ${(props) => props.theme?.white};
    `}

  ${(props) =>
    props.isExpandable &&
    css`
      div {
        flex: 1;
      }
    `}
`;

const TableLineHeader = styled.div`
  display: none !important;
  font-weight: bold;

  @media only screen and (max-width: 1023px) {
    display: flex !important;
    flex: 0 0 120px;
  }
`;

const TableLineChild = styled.div`
  @media only screen and (max-width: 1023px) {
    display: flex !important;
    flex: auto;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SearchContainer = styled(Col)`
  min-width: 230px !important;
`;

const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ActionButtonsContainer = styled(Col)`
  white-space: nowrap;
  text-align: end;
  @media (max-width: 767px) {
    text-align: start;
  }
`;

const DefaultTable = (props) => {
  const {
    headers,
    children,
    hasSearch,
    haspagination,
    haspagesizefilter,
    maincolor,
    totalitems,
    getData,
    searchedParams,
    noLineBorder,
    topControlsClass,
    smallTable,
    responsivetable,
    isExpandable,
    loading,
    isRtl,
    showNoDataLabel,
    fixedLayout,
    lineHeaderEnabled = true,
  } = props;

  const { t } = useTranslation();

  const { page, limit, search, orderBy, order, filter } = searchedParams;
  const [searchedWord, setSearchedWord] = useState(searchedParams.search);

  const tablePageLimits = useMemo(
    () =>
      tablePageSizes.map((pageSize) => ({
        id: pageSize,
        value: pageSize,
      })),
    [tablePageSizes]
  );

  const pageLimit = useMemo(
    () => ({
      id: searchedParams.limit || tablePageSizes[0],
      value: searchedParams.limit || tablePageSizes[0],
    }),
    [searchedParams?.limit]
  );

  const changePageSizeHandler = (pageLimitObj) => {
    const params = {
      ...searchedParams,
      page: 0,
      filter: filter,
      limit: pageLimitObj.value,
      search: search,
      orderBy: orderBy,
      order: order,
    };
    getData(params);
  };

  const onSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const params = {
        ...searchedParams,
        page: page,
        limit: limit,
        filter: filter,
        search: event.target.value,
        orderBy: orderBy,
        order: order,
      };
      getData(params);
    }
  };

  const clearInput = () => {
    setSearchedWord("");
    const params = {
      ...searchedParams,
      page: 0,
      limit: limit,
      filter: filter,
      search: "",
      orderBy: orderBy,
      order: order,
    };
    getData(params);
  };

  const onChangeSearch = (value) => {
    setSearchedWord(value);
    searchOnTable(value);
  };

  const searchOnTable = useCallback(
    debounce((value) => {
      const params = {
        ...searchedParams,
        page: 0,
        limit: limit,
        filter: filter,
        search: value,
        orderBy: orderBy,
        order: order,
      };
      getData(params);
    }, 500),
    [getData, searchedParams]
  );

  const isActive = (header, orderBy, order, orderType) => {
    if (header.key !== orderBy || order !== orderType) {
      return "silver";
    }
    return "";
  };

  const onSortHandler = (header) => {
    if (header.isSortable) {
      const params = {
        ...searchedParams,
        page: 0,
        limit: limit,
        filter: filter,
        search: search,
        orderBy: header.key,
        order: order === orderType.ASC ? orderType.DESC : orderType.ASC,
      };
      getData(params);
    }
  };

  const renderActionButtons = (child) => {
    if (child?.type === ActionButtons) {
      const { children } = child.props;
      return (
        <>
          {children && (
            <ActionButtons>
              {Children.map(children, (child) => (
                <span>{child}</span>
              ))}
            </ActionButtons>
          )}
        </>
      );
    }
  };

  const renderFilter = (child) => {
    if (child && child.type === Filter) {
      const { children } = child.props;
      return (
        <Row>
          {Children.map(children, (child) => (
            <StyledCol>{child}</StyledCol>
          ))}
        </Row>
      );
    }
  };

  const renderLine = (child, lineIndex) => {
    const renderInfo = (data) => {
      if (data?.props?.children !== null && data?.props?.children !== "") {
        return data;
      }
      return "-";
    };

    if (child) {
      const { children } = child.props;

      if (child.type === Line) {
        return (
          <Line>
            <tr>
              {children?.map((child, index) => {
                return (
                  <TableLine key={index} responsivetable={responsivetable}>
                    {lineHeaderEnabled && (
                      <TableLineHeader>{headers[index].label}</TableLineHeader>
                    )}
                    <TableLineChild>{renderInfo(child)}</TableLineChild>
                  </TableLine>
                );
              })}
            </tr>
          </Line>
        );
      } else if (child.type === ExpandableLine) {
        return (
          <ExpandableLines
            {...{ children }}
            {...{ headers }}
            lineIndex={lineIndex}
          />
        );
      }
    }
  };

  return (
    <>
      <TableActions
        className={`g-0${topControlsClass ? topControlsClass : ""}`}
      >
        {hasSearch ? (
          <SearchContainer xs lg="2">
            <SearchInputContainer>
              <input
                data-testid={
                  props["data-testid"]
                    ? `${props["data-testid"]}-searchComponent`
                    : "searchComponent"
                }
                className="form-control customSearch"
                type="text"
                placeholder={t("appText.table.search")}
                value={searchedWord}
                onKeyDown={(e) => onSearchKeyDown(e)}
                onChange={(e) => onChangeSearch(e.target.value)}
              />
              {searchedWord && (
                <button
                  data-testid={
                    props["data-testid"]
                      ? `${props["data-testid"]}-clearBtn`
                      : "clearBtn"
                  }
                  className="bg-transparent border-0"
                  onClick={() => clearInput()}
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </SearchInputContainer>
          </SearchContainer>
        ) : null}

        <Col md="auto">
          {Children?.map(children, (child) => renderFilter(child))}
        </Col>
        <ActionButtonsContainer>
          {Children?.map(children, (child) => renderActionButtons(child))}
        </ActionButtonsContainer>
      </TableActions>
      <div className="row">
        <StyledTable
          data-testid={props["data-testid"] || "defaultTable"}
          responsive
          $noLineBorder={noLineBorder}
          $smallTable={smallTable}
          responsivetable={responsivetable}
          $fixedLayout={fixedLayout}
        >
          <thead>
            <tr>
              {isExpandable && <th></th>}
              {headers &&
                headers.map((header, index) => (
                  <th
                    key={index}
                    data-testid={`headerSortable-${index} `}
                    className={`${header.isSortable ? "cursor-pointer" : ""} `}
                    onClick={() => onSortHandler(header)}
                  >
                    <span>
                      {header.label} {header.tooltip}
                    </span>
                    {header.isSortable && (
                      <>
                        <i
                          className={`fas fa-caret-up ms-3 me-2 ${isActive(
                            header,
                            orderBy,
                            order,
                            orderType.ASC
                          )}`}
                        />
                        <i
                          className={`fas fa-caret-down ${isActive(
                            header,
                            orderBy,
                            order,
                            orderType.DESC
                          )}`}
                        />
                      </>
                    )}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {!loading &&
              Children?.map(children, (child, index) =>
                renderLine(child, index)
              )}
          </tbody>
        </StyledTable>
      </div>
      <StyledInfo>
        {totalitems === 0 && showNoDataLabel && (
          <span className="pb-2">{t("appText.noData")}</span>
        )}
        {(loading || totalitems === null) && <LoadingSpinner />}
      </StyledInfo>
      <Row>
        <Col sm={6}>
          {haspagesizefilter && totalitems ? (
            <FormInline
              data-testid="pageSizeComponent"
              className="form-inline mb-1 justify-content-start"
            >
              {t("appText.table.itemsToDisplay")}
              <CustomDropdown
                className="me-2 ms-2"
                value={pageLimit.value}
                options={tablePageLimits}
                onChange={(e) => changePageSizeHandler(e)}
                align={isRtl ? "end" : "start"}
              />
            </FormInline>
          ) : null}
        </Col>
        <Col sm={6}>
          {haspagination && (
            <TablePagination
              specificKey={null}
              specificParams={searchedParams}
              maincolor={maincolor}
              total={totalitems}
              getData={getData}
              small={smallTable}
              isRtl={isRtl}
              loading={loading}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

DefaultTable.defaultProps = {
  responsivetable: 1,
  isExpandable: false,
  showNoDataLabel: true,
};

DefaultTable.propTypes = {
  responsivetable: PropTypes.number,
  isExpandable: PropTypes.bool,
  showNoDataLabel: PropTypes.bool,
};

export default withTheme(DefaultTable);
